import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { grantOrderAccess } from "../../../Services/customerAccessService";

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// Email + "can view" / "receives emails" - at least one of the two must be
// checked (the endpoint requires both fields, but there's no reason to grant
// a no-op access with neither on). Test selection starts empty on every open
// - the admin must deliberately pick which tests to share, rather than
// defaulting to "the whole order" (that default is exactly the bug this
// test-scoping change fixes).
function GrantAccessDialog({ open, orderId, tests = [], testsLoading = false, onClose, onGranted }) {
  const [email, setEmail] = React.useState("");
  const [hasAccess, setHasAccess] = React.useState(true);
  const [receivesEmails, setReceivesEmails] = React.useState(true);
  const [selectedTestIds, setSelectedTestIds] = React.useState(() => new Set());
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setEmail("");
      setHasAccess(true);
      setReceivesEmails(true);
      setSelectedTestIds(new Set());
      setError("");
    }
  }, [open]);

  const toggleTest = (labTestId, checked) => {
    setSelectedTestIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(labTestId);
      else next.delete(labTestId);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    setSelectedTestIds(checked ? new Set(tests.map((t) => t.lab_test_id)) : new Set());
  };

  const canSave =
    isValidEmail(email) &&
    (hasAccess || receivesEmails) &&
    selectedTestIds.size > 0 &&
    !testsLoading &&
    !loading;

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    setError("");
    try {
      const res = await grantOrderAccess(orderId, {
        email: email.trim(),
        hasAccess,
        receivesEmails,
        testIds: Array.from(selectedTestIds),
      });
      onGranted(res?.data || {});
    } catch (err) {
      setError(err.message || "Couldn't grant access.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Grant access</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          sx={{ mt: 1, mb: 1.5 }}
        />

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControlLabel
            control={
              <Checkbox checked={hasAccess} onChange={(e) => setHasAccess(e.target.checked)} disabled={loading} />
            }
            label="Can view this order"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={receivesEmails}
                onChange={(e) => setReceivesEmails(e.target.checked)}
                disabled={loading}
              />
            }
            label="Receives email updates"
          />
        </Box>

        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mt: 2, mb: 0.5 }}>
          Which tests can they access?
        </Typography>
        {testsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : tests.length === 0 ? (
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>No tests found on this order.</Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTestIds.size === tests.length}
                  indeterminate={selectedTestIds.size > 0 && selectedTestIds.size < tests.length}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Select all"
            />
            <FormGroup sx={{ maxHeight: 220, overflowY: "auto", pl: 1 }}>
              {tests.map((test) => (
                <FormControlLabel
                  key={test.lab_test_id}
                  control={
                    <Checkbox
                      checked={selectedTestIds.has(test.lab_test_id)}
                      onChange={(e) => toggleTest(test.lab_test_id, e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={test.test_identifier || test.substrate}
                />
              ))}
            </FormGroup>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave} autoFocus>
          {loading ? "Granting..." : "Grant access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GrantAccessDialog;
